(() => {
  const p0Enabled = document.documentElement.dataset.afvP0 === "true";
  const p0Strings = JSON.parse(document.getElementById("AFVRestockStrings")?.textContent || "{}");
  const emit = (eventName, detail = {}) => {
    document.dispatchEvent(new CustomEvent(eventName, { detail }));

    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: eventName, ...detail });
    }
  };

  window.afvCRO = window.afvCRO || {};
  window.afvCRO.emit = emit;

  const patchQuickViewLoader = () => {
    const QuickViewElement = customElements.get('quick-view');
    if (!QuickViewElement || QuickViewElement.prototype.afvSectionLoader) return;

    QuickViewElement.prototype.afvSectionLoader = true;
    const originalBeforeHide = QuickViewElement.prototype.beforeHide;
    QuickViewElement.prototype.beforeHide = function () { this.afvLoadController?.abort(); return originalBeforeHide?.call(this); };
    QuickViewElement.prototype.quickview = function () {
      this.afvLoadController?.abort();
      const loadController = new AbortController();
      this.afvLoadController = loadController;
      const drawerContent = this.querySelector(this.selector);
      const productUrl = this.getAttribute('data-product-url').split('?')[0];
      const modalUrl = `${productUrl}?view=modal`;

      fetch(modalUrl, { credentials: 'same-origin', signal: loadController.signal })
        .then((response) => {
          if (!response.ok) throw new Error(`Quick Add returned ${response.status}`);
          return response.text();
        })
        .then((responseText) => {
          if (loadController.signal.aborted || this.afvLoadController !== loadController || !this.open) return;
          const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
          const productElement = parsedHTML.querySelector(this.selector);
          if (!productElement) throw new Error('Quick Add section was missing');

          this.setInnerHTML(drawerContent, productElement.innerHTML);
          theme.a11y.trapFocus(this, this.focusElement);
          if (window.Shopify && Shopify.PaymentButton) Shopify.PaymentButton.init();
          document.dispatchEvent(new CustomEvent('quickview:loaded', {
            detail: { productUrl: this.getAttribute('data-product-url') }
          }));
        })
        .catch((error) => {
          if (error.name === 'AbortError') return;
          drawerContent.replaceChildren();
          const fallback = document.createElement('div');
          const message = document.createElement('p');
          const link = document.createElement('a');
          fallback.className = 'afv-quick-add-fallback';
          message.textContent = p0Strings.load_error || 'Quick Add could not load this item.';
          link.className = 'button button--primary';
          link.href = productUrl;
          link.textContent = p0Strings.view_product || 'VIEW PRODUCT';
          fallback.append(message, link);
          drawerContent.appendChild(fallback);
          emit('afv_quick_add_error', { message: error.message });
        });
    };
  };

  patchQuickViewLoader();

  let quickAddPending = false;
  document.addEventListener('afv:cart-error', () => { quickAddPending = false; }, true);

  const prepareP0QuickAdd = (modal) => {
    modal.afvPickerObserver?.disconnect();
    modal.afvPickerAbort?.abort();
    modal.afvPickerAbort = new AbortController();
    const signal = modal.afvPickerAbort.signal;
    const info = modal.querySelector('product-info');
    const form = info && document.getElementById(info.getAttribute('form'));
    if (!info || !form || !modal.querySelector('variant-picker')) return;
    const selected = new Map();
    let confirmed = null;
    let pending = false;
    const prompt = document.createElement('p');
    prompt.className = 'afv-quick-add-prompt';
    prompt.setAttribute('role', 'status');
    prompt.setAttribute('aria-live', 'polite');
    modal.querySelector('variant-picker').before(prompt);
    // The native transition inserts the active picker before a hidden outgoing copy.
    const groups = () => [...(info.querySelector('variant-picker')?.querySelectorAll('fieldset, select') || [])];
    const key = group => group.dataset.optionIndex || group.name || group.id;
    const value = group => group.matches('select') ? group.value : group.querySelector('input:checked')?.value;
    const matches = variant => groups().every((group, index) => {
      const optionIndex = Number(group.dataset.optionIndex?.replace('option', '')) - 1;
      const position = Number.isInteger(optionIndex) && optionIndex >= 0 ? optionIndex : index;
      return selected.has(key(group)) && value(group) === selected.get(key(group)) && variant?.options?.[position] === selected.get(key(group));
    });
    groups().forEach(group => {
      const options = group.matches('select') ? [...group.options] : [...group.querySelectorAll('input[type="radio"]')];
      if (options.length === 1 && !options[0].disabled) selected.set(key(group), options[0].value);
    });
    const ready = () => !pending && confirmed?.available && matches(confirmed) && String(confirmed.id) === form.querySelector('[name="id"]')?.value;
    const sync = () => {
      if (!modal.isConnected) return;
      groups().forEach(group => {
        if (selected.has(key(group))) return;
        if (group.matches('select')) {
          group.selectedIndex = -1;
          group.querySelectorAll('option[selected]').forEach(option => option.removeAttribute('selected'));
        } else {
          group.querySelectorAll('input:checked, input[checked]').forEach(input => { input.checked = false; input.removeAttribute('checked'); });
          const label = group.querySelector('.form__label .font-medium');
          if (label?.textContent) label.textContent = '';
        }
      });
      const canAdd = ready();
      const text = pending ? p0Strings.updating : confirmed && !confirmed.available && matches(confirmed) ? theme.variantStrings.soldOut : p0Strings.choose;
      const selectionText = canAdd ? `${p0Strings.selected} ${[...selected.values()].join(' / ')}` : text;
      if (prompt.textContent !== selectionText) prompt.textContent = selectionText;
      const button = form.querySelector('[type="submit"]');
      if (!button) return;
      if (!canAdd) {
        if (button.getAttribute('aria-disabled') !== 'true') button.setAttribute('aria-disabled', 'true');
        const label = button.querySelector('.btn-text > span, .btn-text');
        if (label && label.textContent !== text) label.textContent = text;
      } else if (button.getAttribute('aria-busy') !== 'true') {
        button.removeAttribute('aria-disabled');
        button.removeAttribute('disabled');
        const label = button.querySelector('.btn-text > span, .btn-text');
        if (label && label.textContent !== theme.variantStrings.addToCart) label.textContent = theme.variantStrings.addToCart;
      }
      form.querySelectorAll('.shopify-payment-button').forEach(control => { control.hidden = !canAdd; control.inert = !canAdd; });
    };
    modal.addEventListener('change', event => {
      const group = event.target.closest('variant-picker fieldset, variant-picker select');
      if (!group || event.target.disabled) return;
      selected.set(key(group), event.target.value);
      pending = true;
      confirmed = null;
      emit('afv_quick_add_variant_selected', { option_name: key(group), option_value: event.target.value || '' });
      sync();
    }, { capture: true, signal });
    form.addEventListener('variant:change', event => {
      if (!matches(event.detail?.variant)) { confirmed = null; pending = false; sync(); return; }
      confirmed = event.detail.variant;
      pending = false;
      sync();
    }, { signal });
    info.addEventListener('afv:variant-error', () => { pending = false; confirmed = null; sync(); }, { signal });
    info.addEventListener('afv:cart-error', () => { pending = false; confirmed = null; sync(); }, { signal });
    document.addEventListener('ajaxProduct:error', () => {
      if (form.querySelector('[aria-busy="true"]')) { pending = false; confirmed = null; sync(); }
    }, { signal });
    document.addEventListener('submit', event => {
      if (event.target !== form) return;
      if (!ready() || form.querySelector('[aria-busy="true"]')) {
        event.preventDefault(); event.stopImmediatePropagation(); sync();
      }
    }, { capture: true, signal });
    modal.addEventListener('click', event => {
      if (!event.target.closest('.shopify-payment-button') || ready()) return;
      event.preventDefault(); event.stopImmediatePropagation();
    }, { capture: true, signal });
    modal.addEventListener('modal:afterHide', () => {
      info.abortController?.abort(); modal.afvLoadController?.abort(); modal.afvPickerObserver?.disconnect(); modal.afvPickerAbort?.abort();
    }, { once: true, signal });
    modal.afvPickerObserver = new MutationObserver(sync);
    modal.afvPickerObserver.observe(info, { childList: true, subtree: true });
    const initial = info.querySelector('[data-selected-variant]');
    try { const variant = JSON.parse(initial?.textContent || 'null'); if (matches(variant)) confirmed = variant; } catch {}
    sync();
  };

  if (p0Enabled) {
    const ProductInfoElement = customElements.get('product-info');
    if (ProductInfoElement) ProductInfoElement.prototype.renderProductInfo = function ({ requestUrl, targetId, callback }) {
      this.abortController?.abort();
      const controller = new AbortController();
      this.abortController = controller;
      const timeout = setTimeout(() => controller.abort(), 15000);
      fetch(requestUrl, { signal: controller.signal })
        .then(response => { if (!response.ok) throw new Error('variant-update'); return response.text(); })
        .then(text => {
          if (controller.signal.aborted || this.abortController !== controller || !this.isConnected) return;
          this.pendingRequestUrl = null;
          const parsed = new DOMParser().parseFromString(text, 'text/html');
          if (!parsed.querySelector('product-info')) throw new Error('variant-content');
          callback?.(parsed);
          if (!this.getSelectedVariant(parsed)) this.dispatchEvent(new CustomEvent('afv:variant-error'));
          document.getElementById(targetId)?.focus({ preventScroll: true });
        })
        .catch(error => {
          if (this.abortController !== controller || !this.isConnected) return;
          this.pendingRequestUrl = null;
          this.productForm?.handleErrorMessage(p0Strings.variant_error);
          this.dispatchEvent(new CustomEvent('afv:variant-error'));
        })
        .finally(() => clearTimeout(timeout));
    };
  }

  const prepareQuickAddPicker = () => {
    const modal = Array.from(document.querySelectorAll('quick-view')).find(
      (element) => !element.hidden && element.querySelector('variant-picker')
    );
    if (!modal) return;
    if (p0Enabled) { prepareP0QuickAdd(modal); return; }

    if (modal.afvPickerObserver) modal.afvPickerObserver.disconnect();
    const selectedOptions = new Set();
    const productInfo = modal.querySelector('product-info');
    const prompt = document.createElement('p');
    prompt.className = 'afv-quick-add-prompt';
    prompt.setAttribute('role', 'status');
    prompt.textContent = 'Select each product option to continue.';

    const initialPicker = modal.querySelector('variant-picker');
    initialPicker.parentElement.insertBefore(prompt, initialPicker);

    const sanitizePicker = () => {
      const picker = modal.querySelector('variant-picker');
      if (!picker) return;

      const optionGroups = Array.from(picker.querySelectorAll('fieldset, select'));
      optionGroups.forEach((group) => {
        const optionKey = group.dataset.optionIndex || group.name || group.id;
        if (selectedOptions.has(optionKey)) return;

        if (group.matches('select')) {
          group.selectedIndex = -1;
          group.querySelectorAll('option[selected]').forEach((option) => option.removeAttribute('selected'));
        } else {
          group.querySelectorAll('input:checked, input[checked]').forEach((input) => {
            input.checked = false;
            input.removeAttribute('checked');
          });
        }
      });

      const allSelected = optionGroups.length > 0 && optionGroups.every((group) => {
        const optionKey = group.dataset.optionIndex || group.name || group.id;
        if (!selectedOptions.has(optionKey)) return false;
        return group.matches('select') ? Boolean(group.value) : Boolean(group.querySelector('input:checked'));
      });

      const form = productInfo
        ? document.forms[productInfo.getAttribute('form')]
        : modal.querySelector('form[is="product-form"]');
      const variantInput = form ? form.querySelector('[name="id"]') : null;
      const submitButton = form ? form.querySelector('[type="submit"]') : null;

      prompt.hidden = allSelected;
      if (!allSelected) {
        if (variantInput) variantInput.value = '';
        if (submitButton) {
          submitButton.setAttribute('disabled', '');
          submitButton.setAttribute('aria-disabled', 'true');
          const submitLabel = submitButton.querySelector('.btn-text > span, .btn-text');
          if (submitLabel && submitLabel.textContent.trim() !== 'SELECT OPTIONS') {
            submitLabel.textContent = 'SELECT OPTIONS';
          }
        }
      }
    };

    modal.addEventListener('change', (event) => {
      const group = event.target.closest('fieldset, select');
      if (group) {
        selectedOptions.add(group.dataset.optionIndex || group.name || group.id);
        emit('afv_quick_add_variant_selected', {
          option_name: group.dataset.optionIndex || group.name || group.id,
          option_value: event.target.value || ''
        });
      }
      sanitizePicker();
    }, true);

    const observer = new MutationObserver(sanitizePicker);
    observer.observe(modal.querySelector('.quick-view__content'), { childList: true, subtree: true });
    modal.afvPickerObserver = observer;
    sanitizePicker();
  };

  document.addEventListener('quickview:loaded', prepareQuickAddPicker);

  document.addEventListener('submit', (event) => {
    if (event.target.closest('.template-collection .quick-add, .template-index .quick-add, quick-view')) {
      const submit = event.target.querySelector('[type="submit"]');
      if (event.defaultPrevented || submit?.disabled || submit?.getAttribute('aria-disabled') === 'true' || submit?.getAttribute('aria-busy') === 'true') return;
      quickAddPending = true;
    }
  }, true);

  document.addEventListener('ajaxProduct:added', () => {
    if (!quickAddPending) return;
    quickAddPending = false;
    emit('afv_quick_add_success');
  });

  document.addEventListener('ajaxProduct:error', (event) => {
    if (!quickAddPending) return;
    quickAddPending = false;
    emit('afv_quick_add_error', {
      message: (event.detail && event.detail.errorMessage) || 'Unable to add item'
    });
  });

  let shippingThresholdReached = null;

  const syncShippingThreshold = (trackTransition = false) => {
    const progress = document.querySelector('[data-afv-shipping-progress]');
    const reached = progress ? progress.dataset.thresholdReached === 'true' : false;

    if (trackTransition && reached && shippingThresholdReached === false) {
      emit('afv_shipping_threshold_reached', {
        cart_total: Number(progress.dataset.cartTotal || 0),
        threshold: Number(progress.dataset.threshold || 0),
        currency: 'USD'
      });
    }

    shippingThresholdReached = reached;
  };

  syncShippingThreshold();
  document.addEventListener('cart:updated', () => syncShippingThreshold(true));

  const cartDrawer = document.getElementById('CartDrawer');
  if (cartDrawer) {
    new MutationObserver(() => syncShippingThreshold(true)).observe(cartDrawer, {
      childList: true,
      subtree: true
    });
  }

  document.addEventListener('click', (event) => {
    const searchTrigger = event.target.closest('.search-drawer-button');
    if (searchTrigger) emit('afv_search_open');

    const searchResult = event.target.closest('[data-afv-search-result]');
    if (searchResult) {
      emit('afv_predictive_search_result_click', {
        result_type: searchResult.dataset.afvSearchResultType || 'unknown',
        context: searchResult.dataset.afvSearchContext || 'search',
        destination: searchResult.href || ''
      });
    }

    const quickAddTrigger = event.target.closest('.template-collection .quick-add button, .template-index .quick-add button');
    if (!quickAddTrigger) return;

    emit('afv_quick_add_open', {
      mode: quickAddTrigger.hasAttribute('aria-controls') ? 'variant-selector' : 'direct'
    });
  });
})();
